import type { DeleteCustomRoleMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface DeleteCustomRoleTRPCInput {
  id: string;
}

/**
 * Maps the tRPC `customRoleDelete` response (camelCase `affectedRows`)
 * back into the Apollo `DeleteCustomRoleMutation` shape so call sites
 * stay compatible across both branches.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  affectedRows: number;
}): DeleteCustomRoleMutation => ({
  delete_custom_role: {
    __typename: 'custom_role_mutation_response',
    affected_rows: trpcData.affectedRows,
  },
});

export const useDeleteCustomRoleTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.customRole.customRoleDelete.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.role.getCustomRoles.queryKey(),
        });
      },
    }),
  });

  return {
    deleteCustomRole: async (
      variables: DeleteCustomRoleTRPCInput
    ): Promise<DeleteCustomRoleMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
